/**
 * Onboarding Steps Service
 *
 * Service layer for onboarding steps API operations.
 * Uses error tuple pattern: [Error | null, Data | null]
 */

import { appConfig } from '@/config/app-config';
import { STORAGE_KEYS } from '@/config/storage-config';
import type {
  OnboardingStepApiResponse,
  DefaultOnboardingStepApiResponse,
  OnboardingStep,
  OnboardingStepStatus,
  TeamLeader,
} from '@/types/onboarding.types';
import {
  mapOnboardingStepFromApi,
  mapDefaultOnboardingStepToInternal,
} from '@/types/onboarding.types';

/**
 * Fetch default onboarding steps (without thread context)
 * Used for initial display before a conversation starts
 *
 * @param agentId - The agent ID used to namespace the route
 * @returns Error tuple: [Error | null, {steps: OnboardingStep[], subdomain?: string} | null]
 */
export async function fetchDefaultOnboardingSteps(
  agentId: string = 'onboarding-agent'
): Promise<[Error | null, {steps: OnboardingStep[], subdomain?: string} | null]> {
  try {
    const url = `${appConfig.API_BASE_URL}/agents/${agentId}/threads/onboarding-steps`;
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN) || '';

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      return [
        new Error(
          `Failed to fetch default onboarding steps: ${response.status} - ${errorText}`
        ),
        null,
      ];
    }

    const data: {steps: DefaultOnboardingStepApiResponse[], subdomain?: string} = await response.json();
    const steps = data.steps.map(mapDefaultOnboardingStepToInternal);

    return [null, {steps, subdomain: data.subdomain}];
  } catch (error) {
    return [error instanceof Error ? error : new Error(String(error)), null];
  }
}

/**
 * Fetch all onboarding steps for a specific thread
 *
 * @param threadId - The ID of the thread
 * @param agentId - The agent ID used to namespace the route
 * @returns Error tuple: [Error | null, {steps: OnboardingStep[], subdomain?: string} | null]
 */
export async function fetchOnboardingSteps(
  threadId: string,
  agentId: string = 'onboarding-agent'
): Promise<[Error | null, {steps: OnboardingStep[], subdomain?: string} | null]> {
  try {
    const url = `${appConfig.API_BASE_URL}/agents/${agentId}/threads/${threadId}/onboarding-steps`;
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN) || '';

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      return [
        new Error(
          `Failed to fetch onboarding steps: ${response.status} - ${errorText}`
        ),
        null,
      ];
    }

    const data: {steps: OnboardingStepApiResponse[], subdomain?: string} = await response.json();
    const steps = data.steps.map(mapOnboardingStepFromApi);

    return [null, {steps, subdomain: data.subdomain}];
  } catch (error) {
    return [error instanceof Error ? error : new Error(String(error)), null];
  }
}

/**
 * Update the status of a specific onboarding step
 *
 * Note: This is primarily for backend/admin use.
 * The UI is read-only and does not expose this functionality to users.
 *
 * @param threadId - The ID of the thread
 * @param stepName - The name of the step to update
 * @param status - The new status
 * @param agentId - The agent ID used to namespace the route
 * @returns Error tuple: [Error | null, boolean]
 */
export async function updateOnboardingStepStatus(
  threadId: string,
  stepName: string,
  status: OnboardingStepStatus,
  agentId: string = 'onboarding-agent'
): Promise<[Error | null, boolean]> {
  try {
    const url = `${appConfig.API_BASE_URL}/agents/${agentId}/threads/${threadId}/onboarding-steps/${stepName}?status=${encodeURIComponent(status)}`;
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN) || '';

    const response = await fetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      return [
        new Error(
          `Failed to update step status: ${response.status} - ${errorText}`
        ),
        false,
      ];
    }

    return [null, true];
  } catch (error) {
    return [error instanceof Error ? error : new Error(String(error)), false];
  }
}

/**
 * Get team leader for a specific thread
 *
 * @param threadId - The ID of the thread
 * @param agentId - The agent ID used to namespace the route
 * @returns Error tuple: [Error | null, TeamLeader | null]
 */
export async function getTeamLeader(
  threadId: string,
  agentId: string = 'onboarding-agent'
): Promise<[Error | null, TeamLeader | null]> {
  try {
    const url = `${appConfig.API_BASE_URL}/agents/${agentId}/threads/${threadId}/team-leader`;
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN) || '';

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data: TeamLeader = await response.json();
    return [null, data];
  } catch (error) {
    return [error instanceof Error ? error : new Error(String(error)), null];
  }
}

